/* eslint no-console: 0 */
import Snackbar from '@material-ui/core/Snackbar';
import CheckIcon from '@material-ui/icons/Check';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { useSetModalTitle } from '../../hooks/modal/useSetModalTitle.ts';
import { useCreateSupportTicketMutation } from '../../hooks/useCreateSupportTicketMutation.ts';
import { useGetCurrentUser, UserInfo } from '../../hooks/useGetCurrentUser.ts';
import screenNavigator from '../../services/ScreenNavigator.js';
import BasicDialog from '../bits/BasicDialog.jsx';
import Button from '../bits/Button.jsx';
import TextField from '../bits/TextField.jsx';
import Loading from './Loading.jsx';

const Alert: React.FC<AlertProps> = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

interface FeatureRequestProps { }

const parseFullName = (user: UserInfo): string => {
  const { firstName, lastName } = user || {};

  if (!firstName && !lastName) {
    return 'Unknown';
  }

  return `${firstName || ''} ${lastName || ''}`;
};

const FeatureRequest: React.FC<FeatureRequestProps> = () => {
  useSetModalTitle('Request a Feature');

  const { data: user, isLoading } =
    useGetCurrentUser({ refetchOnInterval: false, refetchOnWindowFocus: false });
  const [fullName, setFullName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  const history = useHistory();
  const mutation = useCreateSupportTicketMutation();

  // Only set text field values if we get new values from server (which we only should get on mount)
  useEffect(() => user && setFullName(parseFullName(user)), [user?.firstName, user?.lastName]);
  useEffect(() => setEmail(user?.email || ''), [user?.email]);

  const onSave = useCallback((evt: any) => {
    evt.preventDefault();

    mutation.mutate({
      fullName, email, description, ticketType: 'FEATURE_REQUEST',
    }, {
      onSuccess: () => {
        setIsFormSubmitted(true);
      },
      onError: () => {
        setErrorMessage('Unable to submit request.');
      },
    });
  }, [fullName, email, description]);

  const isFormValid = useMemo(
    () => fullName?.length > 0 && email?.length > 0 && description?.length > 0,
    [fullName, email, description],
  );

  const onSuccessDialogClose = useCallback(
    () => screenNavigator.exitCurrentScreen(history),
    [history],
  );
  const onHideErrorBar = useCallback(() => setErrorMessage(null), [setErrorMessage]);

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && user && <>
        <p style={{ fontSize: '0.9em' }}>
          If you have an app feature request or some general feedback, please let us know. We'd love to hear from you!
        </p>
        <div>
          <form onSubmit={onSave}>
            <TextField
              id="description"
              type="text"
              label="Describe your request / feedback"
              value={description}
              fullWidth
              margin="normal"
              disabled={mutation.isLoading}
              multiline={true}
              rows="5"
              onChange={e => setDescription(e.target.value)}
            />

            <div className="login__body__submit">
              <Button
                type="submit"
                disabled={!isFormValid}
                loading={mutation.isLoading}
                color="primary">
                Submit
              </Button>
            </div>
          </form>

          <BasicDialog
            title="Feedback Received"
            open={isFormSubmitted}
            onClose={onSuccessDialogClose}
          >
            <div>
              <div style={{ textAlign: 'center', color: '#3bbb8f' }}>
                <CheckIcon fontSize="large" />
              </div>
              Your feedback has been received. Thank you for helping us make the app better!
            </div>
          </BasicDialog>

          { /* Render an empty <div> if no error message to fix some weird bugs with the Snackbar */ }
          <Snackbar
            open={errorMessage !== null}
            autoHideDuration={6000}
            onClose={onHideErrorBar}
          >
            {errorMessage
              ? <Alert onClose={onHideErrorBar} severity="error">
                {errorMessage}
              </Alert> : <div />}
          </Snackbar>
        </div>
      </>}
    </>
  );
};

export {
  FeatureRequest
};

